import { useCallback, useEffect, useState } from 'react';
import {
  GA4ObjectProperties,
  ObjectEventParams,
  objectPropertiesFromAuction,
  trackGA4ObjectEvent
} from '~/helpers/client/ga4TrackEvent';
import Auction from '../interfaces/Auction';
import { Nullable } from '../types/Nullable';
import { useGA4UserIsHydrated } from './useGA4User';
import { useDebounceGA4Event } from '~/App/shared/hooks/useDebounceGA4Event';

export const useGA4ObjectEvent = <T = ObjectEventParams>(
  event: string,
  auction?: Auction,
  initEventParams: T = {} as T
) => {
  const { ga4User, isHydrated: ga4UserIsHydrated } = useGA4UserIsHydrated();
  const [eventParams, setEventParams] = useState<T>(initEventParams);
  const [lastTrackedObjectId, setlastTrackedObjectId] =
    useState<Nullable<string>>(null);
  const ga4ViewEvent = useDebounceGA4Event();

  const ga4Object = auction && objectPropertiesFromAuction(auction);

  const trackObjectView = useCallback(
    (
      nextGA4Object: GA4ObjectProperties | undefined = ga4Object,
      evParams: T = eventParams
    ) => {
      if (nextGA4Object) {
        const objectViewEventParams: ObjectEventParams = {
          ...evParams,
          object: { ...nextGA4Object }
        };

        trackGA4ObjectEvent(event, objectViewEventParams, ga4User);
      }

      ga4Object && setlastTrackedObjectId(ga4Object.id);
    },
    [event, eventParams, ga4Object, ga4User]
  );

  useEffect(() => {
    if (ga4UserIsHydrated && ga4Object?.id !== lastTrackedObjectId) {
      ga4ViewEvent(trackObjectView);
    }
  }, [
    ga4Object?.id,
    ga4UserIsHydrated,
    ga4ViewEvent,
    lastTrackedObjectId,
    trackObjectView
  ]);

  return { ga4Object, trackObjectView, setEventParams };
};
